import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Fade, useTheme } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  activityOutcomesAtom,
  openSnackbarAdviseAtom,
  reloadAtom,
  selectedBranchAtom,
  selectedCustomerIdAtom,
} from '../../Atoms';
import { getMonday, getFriday, roundToHalfHour } from '../../Helpers/dates';
import '!style-loader!css-loader!../../Styles/calendar-custom.scss';
import AppointmentDialog from '../../Components/Dialog/AppointmentDialog';
import CalendarPaper from './Components/CalendarPaper';
import EventDetailsDialog from './Components/EventDetailsDialog';
import useAppointments from './hooks/useAppointments';

const getWeekDayRange = () => [getMonday(), getFriday()];

// Combines a date and a time into a single Date without mutating either input.
const combineDateTime = (date, time) => {
  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return combined;
};

const AppointmentsComponents = function AppointmentsComponents({ userRoles }) {
  const intl = useIntl();
  const theme = useTheme();

  const isOperator = Boolean(
    userRoles?.find(
      ({ roleId }) => roleId === 'OPERATOR' || roleId === 'LIMITEDOPERATOR'
    )
  );

  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const selectedCustomerId = useAtomValue(selectedCustomerIdAtom);
  const activityOutcomes = useAtomValue(activityOutcomesAtom);
  const [reload, setReload] = useAtom(reloadAtom);

  const [appointmentOutcomes, setAppointmentOutcomes] = useState([]);
  const [addDialog, setAddDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [edit, setEdit] = useState(false);
  const [range, setRange] = useState(getWeekDayRange());
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);

  const {
    events,
    loading,
    error,
    refetch,
    createAppointment,
    updateAppointment,
    endAppointment,
  } = useAppointments({
    selectedBranch,
    range,
    enabled: !selectedCustomerId,
  });

  const notify = (message, severity) =>
    setOpenSnackbarAdvise({ message, severity });

  const handleChangeRange = (providedRange) => {
    if (!Array.isArray(providedRange)) {
      setRange([providedRange.start, providedRange.end]);
    } else if (providedRange.length > 0) {
      const rangeEnd = new Date(providedRange[providedRange.length - 1]);
      rangeEnd.setDate(rangeEnd.getDate() + 1); // include the last visible day
      setRange([providedRange[0], rangeEnd]);
    }
  };

  const handleSubmitNew = (data) => {
    createAppointment(
      {
        entityId: data.customer.id,
        scheduledOn: combineDateTime(data.date, data.time).toISOString(),
        notes: data.desc,
        assignee: data.assignee,
        remindBefore: data.remindBefore,
      },
      {
        onSuccess: () => {
          setAddDialog(null);
          notify(intl.messages.APPOINTMENT_CREATED, 'success');
        },
        onError: () => notify(intl.messages.CREATE_APP_ERROR, 'error'),
      }
    );
  };

  const handleSubmitEdit = (data) => {
    updateAppointment(
      selectedAppointmentId,
      {
        entityId: data.customer.id,
        scheduledOn: combineDateTime(data.date, data.time).toISOString(),
        notes: data.desc,
        assignee: data.assignee,
        remindBefore: data.remindBefore,
      },
      {
        onSuccess: () => {
          setEditDialog(null);
          setEdit(false);
          notify(intl.messages.APPOINTMENT_EDITED, 'success');
        },
        onError: () => notify(intl.messages.EDIT_APP_ERROR, 'error'),
      }
    );
  };

  // Drag-to-reschedule: only the start time changes; duration is fixed by type.
  const handleRescheduleEvent = ({ event, start }) => {
    updateAppointment(
      event.appointmentId,
      {
        entityId: event.entityId,
        scheduledOn: new Date(start).toISOString(),
        notes: event.desc,
        assignee: event.assignee,
        remindBefore: event.remindBefore,
      },
      {
        onSuccess: () => notify(intl.messages.APPOINTMENT_EDITED, 'success'),
        onError: () => notify(intl.messages.EDIT_APP_ERROR, 'error'),
      }
    );
  };

  const handleEndAppointment = (payload, onSuccess) => {
    endAppointment(selectedAppointmentId, payload, {
      onSuccess: () => {
        setEditDialog(null);
        notify(intl.messages.APPOINTMENT_COMPLETED, 'info');
        if (onSuccess) onSuccess();
      },
      onError: () => notify(intl.messages.ERROR, 'error'),
    });
  };

  // Refresh when an external trigger requests it (e.g. returning from a customer).
  useEffect(() => {
    if (selectedCustomerId) {
      sessionStorage.setItem('currentSection', 'APPOINTMENT');
    }
  }, [selectedCustomerId]);

  useEffect(() => {
    if (reload && !selectedCustomerId) {
      setReload(false);
      refetch();
    }
  }, [reload, selectedCustomerId, refetch, setReload]);

  useEffect(() => {
    if (error) notify(intl.messages.ERROR, 'error');
  }, [error]);

  const appointmentColors = useMemo(
    () =>
      theme.custom.generateConsistentUniqueColors(
        activityOutcomes?.APPOINTMENT?.map(({ name }) => name) || []
      ),
    [activityOutcomes, theme.custom]
  );

  useEffect(() => {
    if (Object.keys(activityOutcomes || {}).length > 0) {
      setAppointmentOutcomes(
        activityOutcomes?.APPOINTMENT?.map((outcome, index) => ({
          ...outcome,
          color: appointmentColors[index],
        }))
      );
    }
  }, [activityOutcomes, appointmentColors]);

  return (
    <Fade in={!selectedCustomerId}>
      <div
        style={{
          display: selectedCustomerId ? 'none' : 'inherit',
          width: 'auto',
          padding: '1rem',
          height: '100%',
        }}
      >
        <CalendarPaper
          events={events}
          loading={loading}
          handleChangeRange={handleChangeRange}
          handleOpenAddDialog={() => setAddDialog({})}
          onSelectEvent={(event) => {
            setEditDialog(event);
            setSelectedAppointmentId(event?.appointmentId);
          }}
          onSelectSlot={(slotInfo) => {
            const { hours, minutes } = roundToHalfHour(slotInfo?.start);
            const start = new Date(slotInfo.start);
            start.setHours(hours, minutes, 0, 0);
            setAddDialog({ ...slotInfo, start });
          }}
          onEventDrop={handleRescheduleEvent}
        />
        <AppointmentDialog
          title="NEW_APPOINTMENT"
          open={Boolean(addDialog)}
          defaultData={addDialog}
          onClose={() => setAddDialog(null)}
          selectedBranch={selectedBranch}
          onSubmit={handleSubmitNew}
          isOperator={isOperator}
        />
        <AppointmentDialog
          title="CHANGE_EVENT"
          defaultData={editDialog}
          open={Boolean(editDialog && edit)}
          onClose={() => {
            setEditDialog(null);
            setEdit(false);
          }}
          selectedBranch={selectedBranch}
          onSubmit={handleSubmitEdit}
          isOperator={isOperator}
        />
        <EventDetailsDialog
          open={Boolean(editDialog && !edit)}
          onClose={() => setEditDialog(null)}
          outcomes={appointmentOutcomes}
          handleEndAppointment={handleEndAppointment}
          setEdit={setEdit}
          event={editDialog}
        />
      </div>
    </Fade>
  );
};

export default AppointmentsComponents;
